import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const statusStyles = {
  active:         { bg: '#ECFDF5', color: '#065F46', dot: '#10B981' },
  available:      { bg: '#EFF6FF', color: '#1E40AF', dot: '#3B82F6' },
  pending:        { bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  decommissioned: { bg: '#F5F3FF', color: '#5B21B6', dot: '#8B5CF6' },
  recycling:      { bg: '#FFF1F2', color: '#9F1239', dot: '#F43F5E' },
};

const BTN = ({ onClick, children, variant="default", style={} }) => {
  const base = { display:"inline-flex", alignItems:"center", gap:"5px", padding:"6px 14px", borderRadius:"6px", fontSize:"12px", fontWeight:"500", cursor:"pointer", border:"1px solid" };
  const variants = {
    default: { background:"#fff", borderColor:"#E5E7EB", color:"#374151" },
    primary: { background:"#1A3A5C", borderColor:"#1A3A5C", color:"#fff" },
    orange:  { background:"#EA6F2E", borderColor:"#EA6F2E", color:"#fff" },
    danger:  { background:"#EF4444", borderColor:"#EF4444", color:"#fff" },
  };
  return <button onClick={onClick} style={{...base, ...variants[variant], ...style}}>{children}</button>;
};

function SortIcon({ active, dir }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
      style={{ opacity: active ? 1 : 0.3, marginLeft:"4px", flexShrink:0 }}>
      {active && dir === "desc" ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
    </svg>
  );
}

function ItemizedTable({ items }) {
  const [sortCol, setSortCol] = useState("category");
  const [sortDir, setSortDir] = useState("asc");
  const grouped = {};
  (items || []).forEach(i => {
    const key = (i.category || "Unknown") + "___" + (i.model || "Unknown");
    if (!grouped[key]) grouped[key] = { category: i.category || "—", description: i.model || "—", quantity: 0 };
    grouped[key].quantity += (parseInt(i.quantity) || 1);
  });
  const rows = Object.values(grouped).sort((a, b) => {
    const av = (a[sortCol] || "").toString().toLowerCase();
    const bv = (b[sortCol] || "").toString().toLowerCase();
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });
  const total = rows.reduce((sum, r) => sum + r.quantity, 0);
  const handleSort = col => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const thStyle = { padding:"8px 12px", textAlign:"left", fontSize:"11px", fontWeight:"600", color:"#6B7280", letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" };
  return (
    <div style={{ border:"1px solid #E5E7EB", borderRadius:"8px", overflow:"hidden", background:"#fff", display:"inline-block", minWidth:"400px" }}>
      <table style={{ width:"auto", borderCollapse:"collapse", fontSize:"12px" }}>
        <thead>
          <tr style={{ background:"#F9FAFB", borderBottom:"1px solid #E5E7EB" }}>
            <th style={{...thStyle, textAlign:"center", width:"60px"}} onClick={() => handleSort("quantity")}><span style={{ display:"inline-flex", alignItems:"center" }}>Qty<SortIcon active={sortCol==="quantity"} dir={sortDir}/></span></th>
            <th style={{...thStyle, width:"110px"}} onClick={() => handleSort("category")}><span style={{ display:"inline-flex", alignItems:"center" }}>Category<SortIcon active={sortCol==="category"} dir={sortDir}/></span></th>
            <th style={thStyle} onClick={() => handleSort("description")}><span style={{ display:"inline-flex", alignItems:"center" }}>Model<SortIcon active={sortCol==="description"} dir={sortDir}/></span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} style={{ borderBottom: idx < rows.length-1 ? "1px solid #F3F4F6":"none", background: idx%2===0?"#fff":"#FAFAFA" }}
              onMouseEnter={e => e.currentTarget.style.background="#F0F4FF"}
              onMouseLeave={e => e.currentTarget.style.background=idx%2===0?"#fff":"#FAFAFA"}>
              <td style={{ padding:"9px 12px", color:"#374151", textAlign:"center", fontWeight:"600" }}>{row.quantity}</td>
              <td style={{ padding:"9px 12px", color:"#374151", fontWeight:"500" }}>{row.category}</td>
              <td style={{ padding:"9px 12px", color:"#111827", fontWeight:"600" }}>{row.description}</td>
            </tr>
          ))}
          <tr style={{ background:"#F9FAFB", borderTop:"2px solid #E5E7EB" }}>
            <td style={{ padding:"9px 12px", textAlign:"center", fontSize:"12px", fontWeight:"700", color:"#1A3A5C" }}>{total}</td>
            <td colSpan="2" style={{ padding:"9px 12px", fontSize:"12px", fontWeight:"700", color:"#111827" }}>Total</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InventoryTable({ items, showAssignee=false, isAdmin=false, onEdit, onDelete, selectedIds, onToggleSelect, onToggleAll }) {
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState("category");
  const [sortDir, setSortDir] = useState("asc");
  const categories = ["all", ...new Set(items.map(i => i.category).filter(Boolean))];
  const filtered = (catFilter === "all" ? items : items.filter(i => i.category === catFilter))
    .filter(i => {
      if (!search) return true;
      const q = search.trim().toLowerCase();
      return [
        i.brand, i.model, i.item_type, i.serial_number, i.notes,
        i.first_name, i.last_name, i.office_number, i.office_name,
        i.first_name && i.last_name ? i.first_name+" "+i.last_name : ""
      ].some(v => v && v.toString().toLowerCase().includes(q));
    })
    .slice().sort((a, b) => {
      const av = (a[sortCol] || "").toString().toLowerCase();
      const bv = (b[sortCol] || "").toString().toLowerCase();
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  const handleSort = col => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const thStyle = { padding:"8px 12px", textAlign:"left", fontSize:"11px", fontWeight:"600", color:"#6B7280", letterSpacing:"0.04em", textTransform:"uppercase", cursor:"pointer", userSelect:"none", whiteSpace:"nowrap" };
  const allSelected = filtered.length > 0 && filtered.every(i => selectedIds.includes(i.id));
  return (
    <>
      <div style={{ marginBottom:"12px", position:"relative" }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" style={{ position:"absolute", left:"10px", top:"50%", transform:"translateY(-50%)", pointerEvents:"none" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by brand, model, serial #, user..." style={{ width:"320px", padding:"8px 10px 8px 30px", border:"1px solid #E5E7EB", borderRadius:"6px", fontSize:"12px", color:"#111827", outline:"none", boxSizing:"border-box" }}/>
        {search && <button onClick={() => setSearch("")} style={{ position:"absolute", right:"8px", top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"16px", lineHeight:1 }}>×</button>}
      </div>
      <div style={{ display:"flex", gap:"4px", marginBottom:"14px", borderBottom:"1px solid #F3F4F6" }}>
        {categories.map(cat => {
          const count = cat === "all" ? items.length : items.filter(i => i.category === cat).length;
          const active = catFilter === cat;
          return (
            <button key={cat} onClick={() => setCatFilter(cat)} style={{ padding:"5px 12px", fontSize:"11px", fontWeight: active ? "600" : "400", color: active ? "#EA6F2E" : "#6B7280", background:"none", border:"none", borderBottom: active ? "2px solid #EA6F2E" : "2px solid transparent", cursor:"pointer", textTransform:"capitalize", marginBottom:"-1px", whiteSpace:"nowrap" }}>
              {cat === "all" ? "All" : cat}
              <span style={{ marginLeft:"4px", fontSize:"10px", color: active ? "#EA6F2E" : "#9CA3AF" }}>{count}</span>
            </button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <div style={{ textAlign:"center", padding:"48px", color:"#9CA3AF", fontSize:"13px" }}>No items found.</div>
      ) : (
        <div style={{ border:"1px solid #E5E7EB", borderRadius:"8px", overflow:"hidden", background:"#fff" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px" }}>
            <thead>
              <tr style={{ background:"#F9FAFB", borderBottom:"1px solid #E5E7EB" }}>
                {isAdmin && <th style={{ padding:"8px 12px", width:"32px" }}><input type="checkbox" checked={allSelected} onChange={() => onToggleAll(filtered.map(i => i.id))} style={{ accentColor:"#EA6F2E" }}/></th>}
                {showAssignee && <th style={thStyle} onClick={() => handleSort("first_name")}><span style={{ display:"inline-flex", alignItems:"center" }}>Assigned To<SortIcon active={sortCol==="first_name"} dir={sortDir}/></span></th>}
                <th style={thStyle} onClick={() => handleSort("category")}><span style={{ display:"inline-flex", alignItems:"center" }}>Category<SortIcon active={sortCol==="category"} dir={sortDir}/></span></th>
                <th style={thStyle} onClick={() => handleSort("item_type")}><span style={{ display:"inline-flex", alignItems:"center" }}>Type<SortIcon active={sortCol==="item_type"} dir={sortDir}/></span></th>
                <th style={thStyle} onClick={() => handleSort("brand")}><span style={{ display:"inline-flex", alignItems:"center" }}>Brand<SortIcon active={sortCol==="brand"} dir={sortDir}/></span></th>
                <th style={thStyle} onClick={() => handleSort("model")}><span style={{ display:"inline-flex", alignItems:"center" }}>Model<SortIcon active={sortCol==="model"} dir={sortDir}/></span></th>
                <th style={{...thStyle, textAlign:"center", width:"50px"}} onClick={() => handleSort("quantity")}><span style={{ display:"inline-flex", alignItems:"center" }}>Qty<SortIcon active={sortCol==="quantity"} dir={sortDir}/></span></th>
                <th style={{...thStyle, width:"130px"}} onClick={() => handleSort("serial_number")}><span style={{ display:"inline-flex", alignItems:"center" }}>Serial #<SortIcon active={sortCol==="serial_number"} dir={sortDir}/></span></th>
                <th style={{...thStyle, width:"110px"}} onClick={() => handleSort("office_number")}><span style={{ display:"inline-flex", alignItems:"center" }}>Location<SortIcon active={sortCol==="office_number"} dir={sortDir}/></span></th>
                <th style={{...thStyle, width:"110px"}} onClick={() => handleSort("status")}><span style={{ display:"inline-flex", alignItems:"center" }}>Status<SortIcon active={sortCol==="status"} dir={sortDir}/></span></th>
                <th style={{...thStyle, width:"160px"}} onClick={() => handleSort("notes")}><span style={{ display:"inline-flex", alignItems:"center" }}>Notes<SortIcon active={sortCol==="notes"} dir={sortDir}/></span></th>
                {isAdmin && <th style={{ padding:"8px 12px", width:"70px" }}></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, idx) => {
                const s = statusStyles[item.status] || statusStyles.active;
                const assignee = item.first_name ? item.first_name + " " + item.last_name : "—";
                const isSelected = selectedIds.includes(item.id);
                return (
                  <tr key={item.id}
                    style={{ borderBottom: idx < filtered.length-1 ? "1px solid #F3F4F6":"none", background: isSelected ? "#FFF7F0" : idx%2===0?"#fff":"#FAFAFA" }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background="#F0F4FF"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = isSelected ? "#FFF7F0" : idx%2===0?"#fff":"#FAFAFA"; }}>
                    {isAdmin && <td style={{ padding:"9px 12px" }}><input type="checkbox" checked={isSelected} onChange={() => onToggleSelect(item.id)} style={{ accentColor:"#EA6F2E" }}/></td>}
                    {showAssignee && <td style={{ padding:"9px 12px", color:"#374151", fontWeight:"500" }}>{assignee}</td>}
                    <td style={{ padding:"9px 12px", color:"#374151", fontWeight:"500" }}>{item.category||"—"}</td>
                    <td style={{ padding:"9px 12px", color:"#111827", fontWeight:"600" }}>{item.item_type||"—"}</td>
                    <td style={{ padding:"9px 12px", color:"#374151" }}>{item.brand||"—"}</td>
                    <td style={{ padding:"9px 12px", color:"#374151" }}>{item.model||"—"}</td>
                    <td style={{ padding:"9px 12px", color:"#374151", textAlign:"center" }}>{item.quantity||1}</td>
                    <td style={{ padding:"9px 12px" }}>
                      {item.serial_number ? <span style={{ fontFamily:"monospace", fontSize:"11px", color:"#374151", background:"#F3F4F6", padding:"2px 6px", borderRadius:"4px" }}>{item.serial_number}</span> : <span style={{ color:"#D1D5DB" }}>—</span>}
                    </td>
                    <td style={{ padding:"9px 12px" }}>
                      {item.office_number ? <span style={{ fontFamily:"monospace", fontSize:"11px", color:"#1A3A5C", background:"#EFF6FF", padding:"2px 6px", borderRadius:"4px" }}>{item.office_number}</span> : <span style={{ color:"#D1D5DB" }}>—</span>}
                    </td>
                    <td style={{ padding:"9px 12px" }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", padding:"2px 8px", borderRadius:"10px", fontSize:"11px", fontWeight:"600", background:s.bg, color:s.color }}>
                        <span style={{ width:"5px", height:"5px", borderRadius:"50%", background:s.dot, flexShrink:0 }}/>
                        {item.status ? item.status.charAt(0).toUpperCase()+item.status.slice(1) : "—"}
                      </span>
                    </td>
                    <td style={{ padding:"9px 12px", color:"#6B7280", fontSize:"11px", maxWidth:"160px" }}>
                      <span style={{ display:"block", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.notes||"—"}</span>
                    </td>
                    {isAdmin && (
                      <td style={{ padding:"9px 12px" }}>
                        <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                          <button onClick={() => onEdit(item)} title="Edit" style={{ background:"none", border:"none", cursor:"pointer", padding:"2px", color:"#9CA3AF", display:"flex" }}
                            onMouseEnter={e => e.currentTarget.style.color="#374151"}
                            onMouseLeave={e => e.currentTarget.style.color="#9CA3AF"}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button onClick={() => onDelete(item)} title="Delete" style={{ background:"none", border:"none", cursor:"pointer", padding:"2px", color:"#FCA5A5", display:"flex" }}
                            onMouseEnter={e => e.currentTarget.style.color="#EF4444"}
                            onMouseLeave={e => e.currentTarget.style.color="#FCA5A5"}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function ItemModal({ item, staffList, officeList, onClose, onSave }) {
  const isEdit = !!item?.id;
  const [form, setForm] = useState({
    category: item?.category || "",
    item_type: item?.item_type || "",
    brand: item?.brand || "",
    model: item?.model || "",
    serial_number: item?.serial_number || "",
    quantity: item?.quantity || 1,
    condition: item?.condition || "Good",
    status: item?.status || "active",
    staff_id: item?.staff_id || "",
    office_id: item?.office_id || "",
    notes: item?.notes || "",
    description: item?.description || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({...f, [k]: v}));

  const handleSave = async () => {
    if (!form.category) return setError("Category is required.");
    if (!form.item_type) return setError("Type is required.");
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (isEdit) {
        await axios.put(process.env.REACT_APP_API_URL + "/inventory-items/" + item.id, form, { headers: { Authorization: "Bearer " + token } });
      } else {
        await axios.post(process.env.REACT_APP_API_URL + "/inventory-items", {...form, clientCode:"caam"}, { headers: { Authorization: "Bearer " + token } });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = { width:"100%", padding:"8px 10px", border:"1px solid #E5E7EB", borderRadius:"6px", fontSize:"12px", color:"#111827", outline:"none", boxSizing:"border-box" };
  const labelStyle = { display:"block", fontSize:"11px", fontWeight:"600", color:"#6B7280", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.04em" };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:"12px", padding:"28px", width:"560px", maxWidth:"95vw", maxHeight:"90vh", overflowY:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" }}>
          <h2 style={{ fontSize:"16px", fontWeight:"700", color:"#111827", margin:0 }}>{isEdit ? "Edit Item" : "Add Inventory Item"}</h2>
          <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:"#9CA3AF", fontSize:"20px", lineHeight:1 }}>×</button>
        </div>
        {error && <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:"6px", padding:"10px 12px", marginBottom:"16px", fontSize:"12px", color:"#B91C1C" }}>{error}</div>}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px" }}>
          <div>
            <label style={labelStyle}>Category *</label>
            <select value={form.category} onChange={e => set("category", e.target.value)} style={inputStyle}>
              <option value="">Select category</option>
              <option>Hardware</option>
              <option>Furniture</option>
              <option>Peripherals</option>
              <option>Networking</option>
              <option>AV Equipment</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Type *</label>
            <input value={form.item_type} onChange={e => set("item_type", e.target.value)} style={inputStyle} placeholder="e.g. Laptop, Chair"/>
          </div>
          <div>
            <label style={labelStyle}>Brand</label>
            <input value={form.brand} onChange={e => set("brand", e.target.value)} style={inputStyle} placeholder="e.g. HP, Dell"/>
          </div>
          <div>
            <label style={labelStyle}>Model</label>
            <input value={form.model} onChange={e => set("model", e.target.value)} style={inputStyle} placeholder="e.g. ProBook 650 G5"/>
          </div>
          <div>
            <label style={labelStyle}>Serial Number</label>
            <input value={form.serial_number} onChange={e => set("serial_number", e.target.value)} style={inputStyle} placeholder="Serial #"/>
          </div>
          <div>
            <label style={labelStyle}>Quantity</label>
            <input type="number" min="1" value={form.quantity} onChange={e => set("quantity", e.target.value)} style={inputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Status</label>
            <select value={form.status} onChange={e => set("status", e.target.value)} style={inputStyle}>
              <option value="active">Active</option>
              <option value="available">Available</option>
              <option value="pending">Pending</option>
              <option value="decommissioned">Decommissioned</option>
              <option value="recycling">Recycling</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Assign To</label>
            <select value={form.staff_id} onChange={e => set("staff_id", e.target.value)} style={inputStyle}>
              <option value="">Unassigned</option>
              {staffList.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Location</label>
            <select value={form.office_id} onChange={e => set("office_id", e.target.value)} style={inputStyle}>
              <option value="">No Location</option>
              {officeList.map(o => <option key={o.id} value={o.id}>{o.office_number}{o.office_name && o.office_name !== o.office_number ? " — " + o.office_name : ""}</option>)}
            </select>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <label style={labelStyle}>Notes</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} style={{...inputStyle, height:"64px", resize:"vertical"}} placeholder="Optional notes"/>
          </div>
        </div>
        <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end", marginTop:"24px" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="primary" onClick={handleSave}>{saving ? "Saving..." : isEdit ? "Save Changes" : "Add Item"}</BTN>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirm({ item, onClose, onConfirm }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(process.env.REACT_APP_API_URL + "/inventory-items/" + item.id, { headers: { Authorization: "Bearer " + token } });
      onConfirm();
    } catch (err) {
      setDeleting(false);
    }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#fff", borderRadius:"12px", padding:"28px", width:"400px", maxWidth:"95vw", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"12px", marginBottom:"16px" }}>
          <div style={{ width:"40px", height:"40px", borderRadius:"50%", background:"#FEF2F2", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
          </div>
          <div>
            <h3 style={{ fontSize:"15px", fontWeight:"700", color:"#111827", margin:0 }}>Delete Item</h3>
            <p style={{ fontSize:"12px", color:"#6B7280", margin:"4px 0 0 0" }}>This action cannot be undone.</p>
          </div>
        </div>
        <p style={{ fontSize:"13px", color:"#374151", marginBottom:"24px" }}>
          Are you sure you want to delete <strong>{item.brand} {item.model || item.item_type}</strong>?
        </p>
        <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end" }}>
          <BTN onClick={onClose}>Cancel</BTN>
          <BTN variant="danger" onClick={handleDelete}>{deleting ? "Deleting..." : "Delete"}</BTN>
        </div>
      </div>
    </div>
  );
}

export default function MyHardware({ isAdmin=false, canSeeAll=false }) {
  // canSeeAll (explicit allowlist) sees the whole register via the Staff tabs,
  // even when not an admin. Editing stays admin-only (isAdmin).
  const seesAll = isAdmin || canSeeAll;
  const [items, setItems] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [officeList, setOfficeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mainTab, setMainTab] = useState("my-hardware");
  const [showReportPanel, setShowReportPanel] = useState(false);
  const [reportFilters, setReportFilters] = useState({ active: true, available: false, pending: false, decommissioned: false, recycling: false });
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => { fetchItems(); if (isAdmin) { fetchStaff(); fetchOffices(); } }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(process.env.REACT_APP_API_URL + "/inventory-items", { headers: { Authorization: "Bearer " + token } });
      setItems(response.data);
    } catch (err) { setError("Failed to load inventory."); }
    finally { setLoading(false); }
  };

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(process.env.REACT_APP_API_URL + "/staff", { headers: { Authorization: "Bearer " + token } });
      setStaffList(response.data);
    } catch (err) { console.error("Failed to load staff"); }
  };

  const fetchOffices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(process.env.REACT_APP_API_URL + "/offices", { headers: { Authorization: "Bearer " + token } });
      setOfficeList(response.data);
    } catch (err) { console.error("Failed to load offices"); }
  };

  const myId = (() => { try { return JSON.parse(localStorage.getItem("user"))?.id; } catch { return null; } })();

  const tabs = seesAll
    ? [{ id:"my-hardware", label:"My Hardware" }, { id:"my-furniture", label:"My Furniture" }, { id:"staff-hardware", label:"Staff Hardware" }, { id:"staff-furniture", label:"Staff Furniture" }, { id:"itemized", label:"Itemized Inventory" }]
    : [{ id:"my-hardware", label:"My Hardware" }, { id:"my-furniture", label:"My Furniture" }, { id:"itemized", label:"Itemized Inventory" }];

  const getTabItems = (tabId) => {
    switch(tabId) {
      case "my-hardware":     return items.filter(i => i.staff_id === myId && i.category !== "Furniture");
      case "my-furniture":    return items.filter(i => i.staff_id === myId && i.category === "Furniture");
      case "staff-hardware":  return items.filter(i => i.staff_id && i.staff_id !== myId && i.category !== "Furniture");
      case "staff-furniture": return items.filter(i => i.staff_id && i.staff_id !== myId && i.category === "Furniture");
      case "itemized":        return items;
      default: return [];
    }
  };

  const getReportData = () => {
    const activeStatuses = Object.entries(reportFilters).filter(([,v]) => v).map(([k]) => k);
    return getTabItems(mainTab).filter(i => activeStatuses.includes(i.status));
  };

  const tabLabel = tabs.find(t => t.id === mainTab)?.label || "";
  const showAssignee = mainTab === "staff-hardware" || mainTab === "staff-furniture";

  const getItemizedRows = (data) => {
    const grouped = {};
    data.forEach(i => {
      const key = (i.category||"Unknown") + "___" + (i.model||"Unknown");
      if (!grouped[key]) grouped[key] = { category: i.category||"—", type: i.model||"—", quantity: 0 };
      grouped[key].quantity += (parseInt(i.quantity) || 1);
    });
    return Object.values(grouped).sort((a,b) => a.category.localeCompare(b.category));
  };

  const exportXLSX = () => {
    const data = getReportData();
    let rows;
    if (mainTab === "itemized") {
      rows = getItemizedRows(data).map(r => ({ Category: r.category, Model: r.type, Quantity: r.quantity }));
    } else {
      rows = data.map(i => ({ ...(showAssignee ? {"Assigned To": i.first_name ? i.first_name+" "+i.last_name : ""} : {}), Category: i.category||"", Type: i.item_type||"", Brand: i.brand||"", Model: i.model||"", Qty: i.quantity||1, "Serial #": i.serial_number||"", Status: i.status||"", Notes: i.notes||"" }));
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tabLabel);
    XLSX.writeFile(wb, "CAAM_" + tabLabel.replace(/\s+/g,"_") + ".xlsx");
  };

  const exportPDF = () => {
    const data = getReportData();
    const doc = new jsPDF({ orientation: mainTab === "itemized" ? "portrait" : "landscape" });
    doc.setFontSize(13); doc.text("CAAM — " + tabLabel, 14, 16);
    doc.setFontSize(9); doc.text("Generated: " + new Date().toLocaleDateString() + "  |  " + data.length + " items", 14, 22);
    let cols, rows;
    if (mainTab === "itemized") {
      cols = ["Category","Model","Quantity"];
      rows = getItemizedRows(data).map(r => [r.category, r.type, r.quantity]);
    } else {
      cols = [...(showAssignee?["Assigned To"]:[]),"Category","Type","Brand","Model","Qty","Serial #","Status","Notes"];
      rows = data.map(i => [...(showAssignee?[i.first_name?i.first_name+" "+i.last_name:""]:[]), i.category||"", i.item_type||"", i.brand||"", i.model||"", i.quantity||1, i.serial_number||"", i.status||"", i.notes||""]);
    }
    autoTable(doc, { head:[cols], body:rows, startY:28, styles:{ fontSize:8 }, headStyles:{ fillColor:[26,58,92] } });
    // Open the PDF inline in a new browser tab (not a download that launches Acrobat).
    const blobUrl = doc.output("bloburl");
    const w = window.open(blobUrl, "_blank");
    if (!w) doc.save("CAAM_" + tabLabel.replace(/\s+/g,"_") + ".pdf"); // popup blocked -> fall back to download
  };

  const printReport = () => {
    const data = getReportData();
    let cols, rows;
    if (mainTab === "itemized") {
      cols = ["Category","Model","Quantity"];
      rows = getItemizedRows(data).map(r => [r.category, r.type, r.quantity]);
    } else {
      cols = [...(showAssignee?["Assigned To"]:[]),"Category","Type","Brand","Model","Qty","Serial #","Status","Notes"];
      rows = data.map(i => [...(showAssignee?[i.first_name?i.first_name+" "+i.last_name:""]:[]), i.category||"", i.item_type||"", i.brand||"", i.model||"", i.quantity||1, i.serial_number||"", i.status||"", i.notes||""]);
    }
    const html = "<html><head><title>CAAM — " + tabLabel + "</title><style>body{font-family:sans-serif;font-size:11px;padding:20px}h2{font-size:14px;margin-bottom:4px}p{color:#666;font-size:10px;margin-bottom:16px}table{width:100%;border-collapse:collapse}th{background:#1A3A5C;color:#fff;padding:6px 8px;text-align:left;font-size:10px}td{padding:5px 8px;border-bottom:1px solid #eee;font-size:10px}tr:nth-child(even)td{background:#f9f9f9}</style></head><body><h2>CAAM — " + tabLabel + "</h2><p>Generated: " + new Date().toLocaleDateString() + " | " + data.length + " items</p><table><thead><tr>" + cols.map(c => "<th>"+c+"</th>").join("") + "</tr></thead><tbody>" + rows.map(r => "<tr>"+r.map(c => "<td>"+c+"</td>").join("")+"</tr>").join("") + "</tbody></table></body></html>";
    const w = window.open("","_blank"); w.document.write(html); w.document.close(); w.print();
  };

  const handleToggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleToggleAll = (ids) => {
    const allSelected = ids.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? selectedIds.filter(id => !ids.includes(id)) : [...new Set([...selectedIds, ...ids])]);
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"300px", gap:"10px" }}>
      <div style={{ width:"18px", height:"18px", border:"2px solid #E0E7FF", borderTopColor:"#1A3A5C", borderRadius:"50%", animation:"spin 0.7s linear infinite" }}/>
      <span style={{ color:"#6B7280", fontSize:"13px" }}>Loading...</span>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding:"32px", textAlign:"center" }}>
      <p style={{ color:"#B71C1C", fontSize:"13px", marginBottom:"12px" }}>{error}</p>
      <button onClick={fetchItems} style={{ padding:"6px 16px", background:"#1A3A5C", color:"#fff", border:"none", borderRadius:"6px", fontSize:"12px", cursor:"pointer" }}>Retry</button>
    </div>
  );

  const currentItems = getTabItems(mainTab);

  return (
    <div style={{ padding:"24px 28px" }}>
      {showAddModal && <ItemModal item={null} staffList={staffList} officeList={officeList} onClose={() => setShowAddModal(false)} onSave={() => { setShowAddModal(false); fetchItems(); }}/>}
      {editItem && <ItemModal item={editItem} staffList={staffList} officeList={officeList} onClose={() => setEditItem(null)} onSave={() => { setEditItem(null); fetchItems(); }}/>}
      {deleteItem && <DeleteConfirm item={deleteItem} onClose={() => setDeleteItem(null)} onConfirm={() => { setDeleteItem(null); fetchItems(); }}/>}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"20px" }}>
        <div>
          <h2 style={{ fontSize:"16px", fontWeight:"700", color:"#111827", margin:"0 0 2px 0", letterSpacing:"-0.01em" }}>Inventory</h2>
          <p style={{ fontSize:"12px", color:"#9CA3AF", margin:0 }}>{currentItems.length} item{currentItems.length!==1?"s":""}{selectedIds.length > 0 ? " — " + selectedIds.length + " selected" : ""}</p>
        </div>
        <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
          {isAdmin && (
            <>
              <div style={{ position:"relative" }}>
                <BTN variant="orange" onClick={() => setShowReportPanel(p => !p)}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  Run Report
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>
                </BTN>
                {showReportPanel && (
                  <div style={{ position:"absolute", right:0, top:"36px", background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px", boxShadow:"0 8px 24px rgba(0,0,0,0.12)", padding:"16px", width:"220px", zIndex:100 }}>
                    <div style={{ fontSize:"11px", fontWeight:"700", color:"#6B7280", letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"10px" }}>Include Statuses</div>
                    {Object.entries(reportFilters).map(([key, val]) => (
                      <label key={key} style={{ display:"flex", alignItems:"center", gap:"8px", padding:"5px 0", cursor:"pointer", fontSize:"12px", color:"#374151" }}>
                        <input type="checkbox" checked={val} onChange={e => setReportFilters(f => ({...f, [key]: e.target.checked}))} style={{ accentColor:"#EA6F2E", width:"13px", height:"13px" }}/>
                        {key.charAt(0).toUpperCase()+key.slice(1)}
                      </label>
                    ))}
                    <div style={{ borderTop:"1px solid #F3F4F6", marginTop:"12px", paddingTop:"12px", display:"flex", flexDirection:"column", gap:"6px" }}>
                      <BTN variant="primary" onClick={() => { exportXLSX(); setShowReportPanel(false); }} style={{ width:"100%", justifyContent:"center" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        Export XLSX
                      </BTN>
                      <BTN variant="primary" onClick={() => { exportPDF(); setShowReportPanel(false); }} style={{ width:"100%", justifyContent:"center" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        Export PDF
                      </BTN>
                      <BTN onClick={() => { printReport(); setShowReportPanel(false); }} style={{ width:"100%", justifyContent:"center" }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                        Print
                      </BTN>
                    </div>
                  </div>
                )}
              </div>
              <BTN variant="primary" onClick={() => setShowAddModal(true)}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Item
              </BTN>
            </>
          )}
          <BTN onClick={fetchItems}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            Refresh
          </BTN>
        </div>
      </div>

      <div style={{ display:"flex", borderBottom:"1px solid #E5E7EB", marginBottom:"20px" }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => { setMainTab(tab.id); setShowReportPanel(false); setSelectedIds([]); }} style={{ padding:"10px 20px", fontSize:"13px", fontWeight: mainTab===tab.id ? "600" : "400", color: mainTab===tab.id ? "#EA6F2E" : "#6B7280", background:"none", border:"none", borderBottom: mainTab===tab.id ? "2px solid #EA6F2E" : "2px solid transparent", cursor:"pointer", marginBottom:"-1px" }}>{tab.label}</button>
        ))}
      </div>

      {mainTab === "itemized"
        ? <ItemizedTable items={items} />
        : currentItems.length === 0
          ? <div style={{ textAlign:"center", padding:"60px", color:"#9CA3AF", fontSize:"13px" }}>No items found.</div>
          : <InventoryTable items={currentItems} showAssignee={showAssignee} isAdmin={isAdmin} onEdit={setEditItem} onDelete={setDeleteItem} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} onToggleAll={handleToggleAll}/>
      }
    </div>
  );
}
